import type { MessageClientIdSource } from '../types/client-message-id.types';
/**
 * Custom metadata key used to reconcile optimistic messages with server payloads.
 */
export const CLIENT_MESSAGE_ID_FIELD = '__communicationsClientMessageId';

const getStringField = (
  source: Record<string, unknown> | undefined,
  field: string
): string | undefined => {
  const value = source?.[field];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/**
 * Reads the optimistic client message id from a message-like object.
 *
 * @param message - Message-like object containing custom fields or metadata.
 * @returns The client message id when present.
 */
export const getMessageClientMessageId = (
  message: MessageClientIdSource
): string | undefined =>
  getStringField(message.customFields, CLIENT_MESSAGE_ID_FIELD) ??
  getStringField(message.metadata, CLIENT_MESSAGE_ID_FIELD);

/**
 * Creates a temporary message id for optimistic outbound messages.
 *
 * @returns A unique temporary id prefixed with `temp-`.
 */
export const createClientMessageId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `temp-${crypto.randomUUID()}`;
  }

  return `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
